import React, { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Calendar, BookPlus } from 'lucide-react';
import { journalAPI } from '../../services/api';
import { useToast } from '../../hooks/use-toast';

const Field = ({ label, value, valueClass = 'text-gray-900' }) => (
  <div>
    <p className="text-sm text-gray-600">{label}</p>
    <p className={`font-semibold ${valueClass}`}>{value}</p>
  </div>
);

const SuggestionCard = ({ suggestion, index, stock, isPro }) => {
  const [adding, setAdding] = useState(false);
  const { toast } = useToast();

  const addToJournal = async () => {
    try {
      setAdding(true);
      await journalAPI.createFromSuggestion(
        stock.symbol,
        suggestion.type,
        suggestion.entry || stock.price,
        1,
        suggestion.reasoning,
      );
      toast({
        title: 'Added to Journal',
        description: `${suggestion.type} suggestion for ${stock.symbol} added to your trade journal.`,
      });
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Could not add to journal. Make sure you are logged in with a Pro account.',
        variant: 'destructive',
      });
    } finally {
      setAdding(false);
    }
  };

  return (
    <div
      className="p-6 bg-gradient-to-r from-purple-50 to-blue-50 rounded-xl border border-purple-200"
      data-testid={`suggestion-${index}`}
    >
      <div className="flex items-center justify-between mb-4">
        <Badge className="bg-purple-600 text-white text-sm px-3 py-1">{suggestion.type}</Badge>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Confidence:</span>
          <span className="font-bold text-purple-600">{suggestion.confidence}%</span>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4 mb-4">
        {suggestion.strike && <Field label="Strike Price" value={`$${suggestion.strike}`} />}
        {suggestion.entry && <Field label="Entry" value={`$${suggestion.entry}`} />}
        {suggestion.expiry && <Field label="Expiry" value={suggestion.expiry} />}
        {suggestion.target && <Field label="Target" value={`$${suggestion.target}`} valueClass="text-green-600" />}
        {suggestion.premium && <Field label="Premium" value={`$${suggestion.premium}`} />}
        {suggestion.stopLoss && <Field label="Stop Loss" value={`$${suggestion.stopLoss}`} valueClass="text-red-600" />}
      </div>
      <div className="mb-4">
        <div className="flex items-center gap-2 mb-2">
          <Calendar size={16} className="text-purple-600" />
          <span className="text-sm font-medium text-gray-700">{suggestion.timeHorizon}</span>
        </div>
      </div>
      <div className="p-4 bg-white rounded-lg mb-4">
        <p className="text-sm text-gray-700 leading-relaxed">{suggestion.reasoning}</p>
      </div>
      {isPro && (
        <Button
          variant="outline"
          size="sm"
          className="w-full border-purple-300 text-purple-700 hover:bg-purple-50"
          disabled={adding}
          onClick={addToJournal}
          data-testid={`suggestion-${index}-add-journal`}
        >
          <BookPlus size={16} className="mr-2" />
          {adding ? 'Adding...' : 'Add to Journal'}
        </Button>
      )}
    </div>
  );
};

const TradeSuggestionsCard = ({ suggestions, loading, stock, isPro }) => (
  <Card data-testid="trade-suggestions-card">
    <CardHeader>
      <CardTitle>AI Trade Suggestions</CardTitle>
    </CardHeader>
    <CardContent className="space-y-4">
      {loading ? (
        <p className="text-center text-gray-500">Loading trade suggestions...</p>
      ) : suggestions.length > 0 ? (
        suggestions.map((suggestion, index) => (
          <SuggestionCard
            key={`${suggestion.type}-${suggestion.entryPrice ?? suggestion.entry ?? index}`}
            suggestion={suggestion}
            index={index}
            stock={stock}
            isPro={isPro}
          />
        ))
      ) : (
        <p className="text-center text-gray-500">No trade suggestions available</p>
      )}
    </CardContent>
  </Card>
);

export default TradeSuggestionsCard;
